import { Minimize2 } from "lucide-react";
import { CSSProperties, useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { StatusPill } from "nazare-ui/components";
import { PageSection, PageSectionAction } from "nazare-ui/components/page-section";
import { cn } from "nazare-ui/utils";

import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { SkeletonRow } from "../../../../skeleton-row";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useDocumentTitle } from "../../../../use-document-title";
import { useWorkflowSchedules, type ScheduleSummary } from "../../../../workflows-api";
import styles from "./scheduled.module.css";

function isWorkflowSchedule(schedule: ScheduleSummary) {
  if (schedule.category) return schedule.category === "workflow";
  return schedule.targets.some((target) => target.kind === "workflow");
}

function nextRun(iso?: string | null) {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export function WorkflowScheduledPage() {
  useDocumentTitle("Scheduled Workflows");
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "all";
  const query = useWorkflowSchedules();
  const all = (query.data?.schedules ?? []).filter(isWorkflowSchedule);
  const rows = all.filter((item) => status === "all" || item.state.toLowerCase() === status);
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const toolbar = useMemo(() => (
    <div className={styles.toolbarRow}><div className={styles.filterBar} role="tablist" aria-label="Filter by status">
      {[{ key: "all", label: "All" }, { key: "enabled", label: "Enabled" }, { key: "disabled", label: "Disabled" }].map((item) => <button key={item.key} type="button" role="tab" aria-selected={status === item.key} className={cn(styles.filterTab, status === item.key && styles.filterTabActive)} onClick={() => setParams(item.key === "all" ? {} : { status: item.key }, { replace: true })}>{item.label}</button>)}
    </div></div>
  ), [setParams, status]);
  useSecondaryHeader(toolbar);
  return (
    <div className={styles.page}>
      <PageSection
        className={styles.card}
        style={{ viewTransitionName: "wf-card-scheduled" } as CSSProperties}
        title="Scheduled workflows"
        count={query.data ? rows.length : undefined}
        actions={
          <PageSectionAction asChild aria-label="Minimise to dashboard" title="Minimise to dashboard">
            <Link to="/workflows" viewTransition><Minimize2 aria-hidden="true" /></Link>
          </PageSectionAction>
        }
      >
        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th className={styles.th}>Name</th><th className={styles.th}>Cron</th><th className={styles.th}>Status</th><th className={`${styles.th} ${styles.thRight}`}>Next run</th></tr></thead>
              <tbody>
                {query.isLoading ? Array.from({ length: 8 }, (_, index) => <SkeletonRow key={index} columns={4} />) : query.error ? (
                  <tr><td className={styles.td} colSpan={4}>Couldn’t load schedules.</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td className={styles.td} colSpan={4}>No workflow schedules match this filter.</td></tr>
                ) : rows.map((schedule) => (
                  <tr key={schedule.name} className={styles.row}><td className={styles.td}><span className={styles.name}>{schedule.label ?? schedule.name}</span></td><td className={`${styles.td} ${styles.cron}`}>{schedule.scheduleExpression}</td><td className={styles.td}><StatusPill tone={schedule.state === "ENABLED" ? "success" : "neutral"}>{schedule.state === "ENABLED" ? "Enabled" : "Disabled"}</StatusPill></td><td className={`${styles.td} ${styles.nextRun}`}>{nextRun(schedule.nextFireTime)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </PageSection>
    </div>
  );
}
